import React, { useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { useRAGStore } from './store/ragStore';
import { Folder, Upload, List, Edit3, Shield, Search, MessageCircle, FileText } from 'lucide-react';
import ContextManagementTab from './components/ContextManagementTab';
import FileUploadTab from './components/FileUploadTab';
import FileListTab from './components/FileListTab';
import CustomRagTab from './components/CustomRagTab';
import ProtectedKeywordsTab from './components/ProtectedKeywordsTab';
import KeywordMappingTab from './components/KeywordMappingTab';
import QueryTab from './components/QueryTab';
import AskTab from './components/AskTab';
import SettingsModal from './components/SettingsModal';
import ErrorDisplay from './components/ErrorDisplay';
import LoadingSpinner from './components/LoadingSpinner';
import GlobalOperationIndicator from './components/GlobalOperationIndicator';

import { useTabNavigation } from './contexts/TabNavigationContext';
import ContextSelector from './components/ContextSelector';

function App() {
  const { 
    isLoading, 
    errorMessage, 
    setError,
    loadContexts
  } = useRAGStore();

  const { selectedTabIndex, setSelectedTabIndex } = useTabNavigation();

  // Initialize app by loading contexts
  useEffect(() => {
    loadContexts();
  }, [loadContexts]); // eslint-disable-line react-hooks/exhaustive-deps

  const { currentContext } = useRAGStore();

  // Use useMemo to prevent tabs array recreation on every render
  const tabs = React.useMemo(() => [
    {
      name: 'Context Management',
      icon: Folder,
      component: <ContextManagementTab />,
      alwaysVisible: true,
    },
    {
      name: 'Protected Keywords',
      icon: Shield,
      component: <ProtectedKeywordsTab />,
      alwaysVisible: false,
    },
    {
      name: 'Keyword Mappings',
      icon: FileText,
      component: <KeywordMappingTab />,
      alwaysVisible: false,
    },
    {
      name: 'File Upload',
      icon: Upload,
      component: <FileUploadTab />,
      alwaysVisible: false,
    },
    {
      name: 'File List',
      icon: List,
      component: <FileListTab />,
      alwaysVisible: false,
    },
    {
      name: 'Custom RAG',
      icon: Edit3,
      component: <CustomRagTab />,
      alwaysVisible: false,
    },
    {
      name: 'Query',
      icon: Search,
      component: <QueryTab />,
      alwaysVisible: false,
    },
    {
      name: 'Ask AI',
      icon: MessageCircle,
      component: <AskTab />,
      alwaysVisible: false,
    },
  ], []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (errorMessage) {
    return (
      <ErrorDisplay 
        error={errorMessage} 
        onDismiss={() => setError(null)}
        title="Application Error"
        variant="error"
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Global Operation Indicator */}
      <GlobalOperationIndicator />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                RAG System - Intelligent Document Search
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <SettingsModal />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar Layout */}
      <div className="flex w-full max-w-7xl mx-auto">
        {/* Left Sidebar - Context Selector */}
        <div className="w-72 min-h-screen bg-white border-r border-gray-200 p-4">
          <ContextSelector />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 px-6 py-8">
          <Tab.Group selectedIndex={selectedTabIndex} onChange={setSelectedTabIndex}>
            <Tab.List className="flex space-x-1 rounded-xl bg-white p-1 shadow-sm border border-gray-200 min-w-0">
              {tabs.map((tab) => {
                // Only show tabs that are always visible or when context is selected
                if (!tab.alwaysVisible && !currentContext) {
                  return null;
                }
                
                return (
                  <Tab
                    key={tab.name}
                    className={({ selected }) =>
                      `min-w-0 flex-1 rounded-lg py-2.5 text-sm font-medium leading-5 transition-all duration-200
                       ${
                         selected
                           ? 'bg-blue-100 text-blue-700 shadow-sm'
                           : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                       }`
                    }
                  >
                                         <div className="flex flex-col items-center justify-center space-y-1 px-1">
                       <tab.icon className="w-4 h-4 flex-shrink-0" />
                       <span className="hidden sm:inline text-center leading-tight text-xs">{tab.name}</span>
                     </div>
                  </Tab>
                );
              })}
            </Tab.List>
            
            <Tab.Panels className="mt-6">
              {tabs.map((tab) => {
                // Only render panels for tabs that are always visible or when context is selected
                if (!tab.alwaysVisible && !currentContext) {
                  return null;
                }
                
                return (
                  <Tab.Panel
                    key={tab.name}
                    className="animate-fade-in"
                  >
                    {tab.component}
                  </Tab.Panel>
                );
              })}
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
      
    </div>
  );
}

export default App;
